/**
 * MaskedSurebetPreview - Component for displaying masked surebet previews
 * 
 * Features:
 * - Fetches data from public /masked endpoint
 * - Shows top 5 masked events (best margin only per event) and one live-demo row
 * - Displays masked event details (teams, dates, bookmakers)
 * - Shows real odds and profit percentages
 * - Encourages users to subscribe for full access
 */

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import SurebetService from '../services/SurebetService';
import SurebetItem from './SurebetItem';
import { getMarginTypeName, getOutcomeName } from '../utils/surebetUtils';
import NumberUtils from '../../../modules/utils/NumberUtils';
import './MaskedSurebetPreview.css';

const EMPTY_PREVIEW = {
  maskedItems: [],
  demoItem: null,
  totalActiveOpportunities: 0,
  totalActiveEvents: 0,
};

const MaskedSurebetPreview = ({ onActivate, onCalculatorClick, showHeader = true, maxItems = 5 }) => {
  const { t, language } = useTranslation();
  const [maskedData, setMaskedData] = useState([]);
  const [demoItem, setDemoItem] = useState(null);
  const [previewTotals, setPreviewTotals] = useState({
    totalActiveOpportunities: 0,
    totalActiveEvents: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch masked data from the public endpoint
  useEffect(() => {
    const fetchMaskedData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await SurebetService.getMaskedMarketsAnalysis();

        if (fetchError) {
          setError(fetchError);
        } else {
          const preview = data && data.maskedItems !== undefined ? data : EMPTY_PREVIEW;
          setMaskedData(preview.maskedItems || []);
          setDemoItem(preview.demoItem || null);
          setPreviewTotals({
            totalActiveOpportunities: preview.totalActiveOpportunities || 0,
            totalActiveEvents: preview.totalActiveEvents || 0,
          });
        }
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchMaskedData();
  }, [showHeader, maxItems]);
  
  // Get the maximum margin value from a surebet
  const getMaxMargin = (surebet) => {
    if (!surebet.margins) return 0;
    const marginValues = Object.values(surebet.margins).map(m => m.value || 0);
    return Math.max(...marginValues, 0);
  };
  
  // Backend returns up to 5 masked events; exclude demo row; sort for display (defensive slice via maxItems)
  const topSurebets = useMemo(() => {
    const demoAnalysisId =
      demoItem?.id !== undefined && demoItem?.id !== null ? Number(demoItem.id) : null;
    const demoEventId =
      demoItem?.event?.id !== undefined && demoItem?.event?.id !== null
        ? Number(demoItem.event.id)
        : null;

    return [...maskedData]
      .filter((surebet) => {
        if (
          demoAnalysisId !== null
          && surebet.id !== undefined
          && surebet.id !== null
          && Number(surebet.id) === demoAnalysisId
        ) {
          return false;
        }
        if (
          demoEventId !== null
          && surebet.event?.id !== undefined
          && surebet.event?.id !== null
          && Number(surebet.event.id) === demoEventId
        ) {
          return false;
        }
        return true;
      })
      .sort((a, b) => getMaxMargin(b) - getMaxMargin(a))
      .slice(0, maxItems);
  }, [maskedData, demoItem, maxItems]);
  
  // One row per event (best margin only) — avoids listing lower derivative markets
  // that make the "top 5" look weaker than they are.
  const topSurebetsCount = topSurebets.length;

  // Statistics: totals from API (full dataset); shown count from visible masked rows
  const statistics = useMemo(() => ({
    totalEvents: previewTotals.totalActiveEvents,
    total: previewTotals.totalActiveOpportunities,
    profitableCount: previewTotals.totalActiveOpportunities,
    shownSurebets: topSurebetsCount,
  }), [previewTotals, topSurebetsCount]);

  /** Highest-value margin for a masked event (preview shows only this one). */
  const getBestMargin = (surebet) => {
    if (!surebet?.margins) return null;
    let best = null;
    Object.entries(surebet.margins).forEach(([marginKey, marginData]) => {
      const value = marginData?.value ?? -Infinity;
      if (!best || value > (best.marginData?.value ?? -Infinity)) {
        best = {
          marginKey,
          marginData,
          marginType: getMarginTypeName(marginKey, language),
        };
      }
    });
    return best;
  };
  
  return (
    <div className="masked-surebet-preview">
      <div className="masked-surebet-container">
        {showHeader && (
          <div className="masked-surebet-header">
            <h2 className="masked-surebet-title">
              {t('landing.maskedPreview.title', 'Топ 5 Пазарни Възможности')}
            </h2>
            <p className="masked-surebet-subtitle">
              {t('landing.maskedPreview.subtitle', `Абонирайте се за достъп до всички ${statistics.total} възможности`)}
            </p>
            
            {/* Show total count of profitable surebets */}
            {!loading && !error && statistics && (
              <div className="masked-surebet-stats">
                <div className="stat-card">
                  <span className="stat-value">{statistics.total || 0}</span>
                  <span className="stat-label">
                    {t('landing.maskedPreview.totalLabel', 'Налични арбитражни възможности')}
                  </span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">{statistics.shownSurebets ?? topSurebetsCount}</span>
                  <span className="stat-label">
                    {t('landing.maskedPreview.shownLabel', 'Показани (топ по печалба)')}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Loading state */}
        {loading && (
          <div className="masked-surebet-loading">
            <div className="loading-spinner"></div>
            <p>{t('landing.maskedPreview.loading', 'Зареждане на данни...')}</p>
          </div>
        )}
        
        {/* Error state */}
        {error && (
          <div className="masked-surebet-error">
            <p>{t('landing.maskedPreview.error', 'Грешка при зареждане на данните')}</p>
            {onActivate && (
              <button className="unlock-btn" onClick={onActivate}>
                {t('landing.maskedPreview.activateButton', 'Активирай абонамент')}
              </button>
            )}
          </div>
        )}
        
        {/* Real masked surebet data - Top by highest profit */}
        {!loading && !error && topSurebets.length > 0 && (
          <>
            {/* Desktop table view */}
            <div className="masked-surebet-table-wrapper">
              <table className="masked-surebet-table">
                <thead>
                  <tr>
                    <th>{t('landing.maskedPreview.eventColumn', 'Събитие')}</th>
                    <th>{t('landing.maskedPreview.marketColumn', 'Пазар')}</th>
                    <th>{t('landing.maskedPreview.oddsColumn', 'Коефициенти')}</th>
                    <th>{t('landing.maskedPreview.profitColumn', 'Печалба')}</th>
                    <th>{t('landing.maskedPreview.actionColumn', 'Действие')}</th>
                  </tr>
                </thead>
                <tbody>
                  {topSurebets.map((surebet) => {
                    const margin = getBestMargin(surebet);
                    if (!margin) return null;

                    return (
                      <tr key={`${surebet.id}-${margin.marginKey}`}>
                        {/* Event column - backend already masks this data */}
                        <td>
                          <div className="masked-surebet-event">
                            <div className="event-teams">
                              <span className="team-name">{surebet.event.teamOne}</span>
                              <span className="vs">vs</span>
                              <span className="team-name">{surebet.event.teamTwo}</span>
                            </div>
                            <div className="event-meta">
                              <span>{surebet.event.date}</span>
                              <span>{surebet.event.time}</span>
                              <span>{surebet.event.championship}</span>
                            </div>
                          </div>
                        </td>
                        
                        {/* Market type column */}
                        <td className="market-type-cell">{margin.marginType}</td>
                        
                        {/* Coefficients column - Show real odds, bookmaker names are masked by backend */}
                        <td>
                          <div className="coefficients-preview">
                            {margin.marginData.coefficients && margin.marginData.coefficients.map((coef, coefIdx) => (
                              <div key={coefIdx} className="coef-item">
                                <span className="coef-label">{getOutcomeName(margin.marginKey, coef.type, language)}:</span>
                                <span className="coef-value">{NumberUtils.formatOdds(coef.value, { decimals: 2 })}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        
                        {/* Profit column - Show real profit */}
                        <td>
                          <div className={`profit-value ${margin.marginData.value > 0 ? 'positive' : 'negative'}`}>
                            {NumberUtils.formatPercentage(margin.marginData.value, { decimals: 2 })}
                          </div>
                        </td>
                        
                        {/* Action column */}
                        <td>
                          {onActivate && (
                            <button 
                              className="unlock-btn"
                              onClick={onActivate}
                            >
                              {t('landing.maskedPreview.unlockButton', 'Активирай сега')}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="masked-surebet-mobile-cards">
              {topSurebets.map((surebet) => {
                const margin = getBestMargin(surebet);
                if (!margin) return null;

                return (
                  <div key={`mobile-${surebet.id}-${margin.marginKey}`} className="masked-surebet-mobile-card">
                    <div className="masked-surebet-mobile-card__row">
                      <span className="masked-surebet-mobile-card__label">
                        {t('landing.maskedPreview.eventColumn', 'Събитие')}
                      </span>
                      <div className="masked-surebet-mobile-card__value">
                        <div className="masked-surebet-event">
                          <div className="event-teams">
                            <span className="team-name">{surebet.event.teamOne}</span>
                            <span className="vs">vs</span>
                            <span className="team-name">{surebet.event.teamTwo}</span>
                          </div>
                          <div className="event-meta">
                            <span>{surebet.event.date}</span>
                            <span>{surebet.event.time}</span>
                            <span>{surebet.event.championship}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="masked-surebet-mobile-card__row">
                      <span className="masked-surebet-mobile-card__label">
                        {t('landing.maskedPreview.marketColumn', 'Пазар')}
                      </span>
                      <div className="masked-surebet-mobile-card__value">
                        <span className="market-type-cell">{margin.marginType}</span>
                      </div>
                    </div>

                    <div className="masked-surebet-mobile-card__row">
                      <span className="masked-surebet-mobile-card__label">
                        {t('landing.maskedPreview.oddsColumn', 'Коефициенти')}
                      </span>
                      <div className="masked-surebet-mobile-card__value">
                        <div className="coefficients-preview">
                          {margin.marginData.coefficients && margin.marginData.coefficients.map((coef, coefIdx) => (
                            <div key={coefIdx} className="coef-item">
                              <span className="coef-label">{getOutcomeName(margin.marginKey, coef.type, language)}:</span>
                              <span className="coef-value">{NumberUtils.formatOdds(coef.value, { decimals: 2 })}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="masked-surebet-mobile-card__row">
                      <span className="masked-surebet-mobile-card__label">
                        {t('landing.maskedPreview.profitColumn', 'Печалба')}
                      </span>
                      <div className="masked-surebet-mobile-card__value">
                        <div className={`profit-value ${margin.marginData.value > 0 ? 'positive' : 'negative'}`}>
                          {NumberUtils.formatPercentage(margin.marginData.value, { decimals: 2 })}
                        </div>
                      </div>
                    </div>

                    {onActivate && (
                      <div className="masked-surebet-mobile-card__action">
                        <button 
                          className="unlock-btn"
                          onClick={onActivate}
                        >
                          {t('landing.maskedPreview.unlockButton', 'Активирай сега')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
        
        {/* Empty state */}
        {!loading && !error && topSurebets.length === 0 && (
          <div className="masked-surebet-empty">
            <p>{t('landing.maskedPreview.empty', 'Няма налични арбитражни възможности в момента')}</p>
            {onActivate && (
              <button className="unlock-btn" onClick={onActivate}>
                {t('landing.maskedPreview.activateButton', 'Активирай абонамент')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Live demo — same layout as unmasked SurebetItem */}
      {!loading && !error && demoItem && (
        <section className="masked-surebet-preview__live-demo" aria-labelledby="live-demo-title">
          <div className="masked-surebet-preview__live-demo-header">
            <h3 id="live-demo-title" className="masked-surebet-preview__live-demo-title">
              {t('landing.liveDemo.title', 'LIVE DEMO: Актуална възможност в момента')}
            </h3>
            <span className="masked-surebet-preview__live-demo-badge">LIVE DEMO</span>
          </div>

          <SurebetItem
            surebet={demoItem}
            onCalculatorClick={onCalculatorClick}
            showLiveDemoMarginInfo
            className="masked-surebet-preview__live-demo-card"
          />

          {onActivate && (
            <div className="masked-surebet-preview__live-demo-footer">
              <button
                type="button"
                className="masked-surebet-preview__live-demo-footer-link"
                onClick={onActivate}
              >
                {t(
                  'landing.liveDemo.footerLink',
                  'Виж останалите арбитражни възможности – Абонирай се сега'
                )}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

MaskedSurebetPreview.propTypes = {
  onActivate: PropTypes.func,
  onCalculatorClick: PropTypes.func,
  showHeader: PropTypes.bool,
  maxItems: PropTypes.number
};

export default MaskedSurebetPreview;

